import React from 'react';
import { Card, CardContent, Typography, Box, Divider } from '@mui/material';

/**
 * Reusable stat card used by the Approvals page and the Payment Matching page.
 * Optional `breakdown` prop renders a small sub-table of label/value rows below
 * the headline value — handy when one count isn't enough context.
 *
 * Row shapes (mix freely within a single card):
 *   { label, value }                  → 2-col: label left, value right.
 *   { label, value, right }           → 3-col: label left, value middle (right-
 *                                       aligned), right far-right.
 *   { count, label, value }           → 3-col descriptor row: count left
 *                                       (muted), label centred, value right.
 *                                       Use when each row pairs a *count* and a
 *                                       *value* against the same descriptor —
 *                                       reads "14   Interest   $ 14.8K".
 */
const StatCard = ({
  title,
  value,
  subtitle,
  Icon,
  color = '#1976d2',
  selected = false,
  onClick,
  breakdown, // optional [{ label, value, right? } | { count, label, value }]
}) => (
  <Card
    onClick={onClick}
    sx={{
      cursor: onClick ? 'pointer' : 'default',
      border: selected ? `2px solid ${color}` : '2px solid transparent',
      boxShadow: selected ? 3 : 1,
      '&:hover': onClick ? { boxShadow: 4 } : {},
      height: '100%',
    }}
  >
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography color="textSecondary" variant="body2">{title}</Typography>
          <Typography variant="h4" component="div">{value}</Typography>
          {subtitle && (
            <Typography variant="body2" color="textSecondary">{subtitle}</Typography>
          )}
        </Box>
        {Icon && (
          <Box sx={{ backgroundColor: `${color}20`, borderRadius: 2, p: 1, ml: 1 }}>
            <Icon sx={{ color, fontSize: 32 }} />
          </Box>
        )}
      </Box>
      {Array.isArray(breakdown) && breakdown.length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {breakdown.map((row, idx) => {
              const hasCount = row.count !== undefined && row.count !== null;
              const hasRight = row.right !== undefined && row.right !== null;

              if (hasCount) {
                // count · label · value — count left (muted), label centred, value right.
                return (
                  <Box
                    key={idx}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      alignItems: 'baseline',
                      columnGap: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}
                    >
                      {row.count}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                    >
                      {row.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, whiteSpace: 'nowrap', textAlign: 'right' }}
                    >
                      {row.value}
                    </Typography>
                  </Box>
                );
              }

              return (
                <Box
                  key={idx}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: hasRight ? 'auto 1fr auto' : '1fr auto',
                    alignItems: 'baseline',
                    columnGap: 1.5,
                  }}
                >
                  <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
                    {row.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, whiteSpace: 'nowrap', textAlign: 'right' }}
                  >
                    {row.value}
                  </Typography>
                  {hasRight && (
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, whiteSpace: 'nowrap', textAlign: 'right' }}
                    >
                      {row.right}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </CardContent>
  </Card>
);

export default StatCard;
